import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocationsState } from "../../containers/LocationsStateContainer";
import {
  Hangar,
  Location,
  Ramp,
  Stack as StackType,
  Tenant,
} from "../../types";
import { AddTenantButton } from "../Settings/HangarSettings/AddTenantButton";
import { HoldingAreaListItem } from "./HoldingAreaListItem";

interface HoldingAreaListProps {
  tenants: Tenant[];
  onAssign: (tenantId: string) => void;
  onEdit: (tenant: Tenant) => void;
  onRemove: (tenantId: string) => void;
  onAdd: (tenant: Tenant | Tenant[]) => void;
  setStack: (stack: StackType) => void;
  location: Location<Hangar | Ramp>;
}

/**
 * List-based holding area component
 * Replaces canvas-based HoldingAreaPresenter
 */
export const HoldingAreaList: React.FC<HoldingAreaListProps> = ({
  tenants,
  onAssign,
  onEdit,
  onRemove,
  onAdd,
  setStack,
  location,
}) => {
  const { t } = useTranslation();
  const { sendToLocation } = useLocationsState();

  // Sort tenants by created_at (newest first) for stable ordering
  const sortedTenants = React.useMemo(() => {
    return [...tenants].sort((a, b) => {
      // Handle missing timestamps by putting them at the end
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      // Sort descending (newest first)
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [tenants]);

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        height: "100%",
        width: "100%",
        overflowY: "auto",
        backgroundColor: "background.default",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 1 }}
      >
        <Typography>{t('screens.multiHangar.holdingAreaList.title', { count: tenants.length })}</Typography>
        <AddTenantButton
          sendToLocation={sendToLocation}
          location={location}
          stack={location.stack}
          setStack={setStack}
          onAdd={onAdd}
        />
      </Stack>

      {/* All Tenants */}
      {tenants.length > 0 && (
        <Stack spacing={1}>
          {sortedTenants.map((tenant) => (
            <HoldingAreaListItem
              key={tenant.id}
              tenant={tenant}
              onAssign={onAssign}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          ))}
        </Stack>
      )}

      {/* Empty State */}
      {tenants.length === 0 && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('screens.multiHangar.holdingAreaList.emptyState')}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
